import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from "@angular/core";
import {WorkModalNavigationDirection} from "../../shared/interfaces/work-modal-navigation.interface";

@Directive({
  selector: "[appWorkSwipeNavigation]",
  standalone: true,
})
export class WorkSwipeNavigationDirective implements OnInit, OnDestroy {
  @Input() swipeNavigationEnabled = false;
  @Input() canSwipePrevious = false;
  @Input() canSwipeNext = false;
  @Input() prepareSwipeNavigation?: (
    direction: WorkModalNavigationDirection
  ) => Promise<boolean>;
  @Output() swipeNavigation = new EventEmitter<WorkModalNavigationDirection>();
  @Output() swipeNavigationDragStarted = new EventEmitter<void>();
  @Output() swipeNavigationDragSettled = new EventEmitter<void>();

  // Matches SwipeDownToCloseDirective's activation threshold.
  private readonly activationDistancePx = 10;
  // Horizontal intent must clearly dominate vertical movement before locking the gesture.
  private readonly horizontalLockRatio = 1.2;
  // A thumb needs less horizontal travel than the 120px vertical close gesture.
  private readonly completeDistancePx = 80;
  private readonly cancelAnimationMs = 200;
  // A blocked direction follows the finger at thirty percent for an elastic edge effect.
  private readonly blockedDragResistance = 0.3;

  private startX = 0;
  private startY = 0;
  private currentDeltaX = 0;
  private gestureState: "idle" | "pending" | "dragging" | "rejected" = "idle";
  private removeTouchStartListener?: () => void;
  private removeTouchEndListener?: () => void;
  private cancelTimeout?: ReturnType<typeof setTimeout>;
  private preparing = false;
  private destroyed = false;
  private readonly touchMoveListener = (event: TouchEvent): void =>
    this.onTouchMove(event);

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {}

  ngOnInit(): void {
    const host = this.elementRef.nativeElement;
    this.removeTouchStartListener = this.renderer.listen(
      host,
      "touchstart",
      (event: TouchEvent) => this.onTouchStart(event)
    );
    this.removeTouchEndListener = this.renderer.listen(host, "touchend", () =>
      this.onTouchEnd()
    );
    host.addEventListener("touchmove", this.touchMoveListener, {
      passive: false,
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.removeTouchStartListener?.();
    this.removeTouchEndListener?.();
    this.elementRef.nativeElement.removeEventListener(
      "touchmove",
      this.touchMoveListener
    );
    if (this.cancelTimeout !== undefined) {
      clearTimeout(this.cancelTimeout);
    }
  }

  private onTouchStart(event: TouchEvent): void {
    if (
      !this.swipeNavigationEnabled ||
      this.preparing ||
      this.shouldIgnoreTouch(event)
    ) {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    this.clearCancelAnimation();
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.currentDeltaX = 0;
    this.gestureState = "pending";
  }

  private shouldIgnoreTouch(event: TouchEvent): boolean {
    const target = event.target;
    if (!(target instanceof Element)) {
      return true;
    }
    return (
      target.closest(
        "swiper-container, input, textarea, select, [data-swipe-navigation-ignore]"
      ) !== null
    );
  }

  private onTouchMove(event: TouchEvent): void {
    if (this.gestureState === "idle" || this.gestureState === "rejected") {
      return;
    }

    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - this.startX;
    const deltaY = touch.clientY - this.startY;
    this.currentDeltaX = deltaX;
    if (this.gestureState === "pending") {
      this.resolvePendingGesture(deltaX, deltaY);
    }
    if (this.gestureState !== "dragging") {
      return;
    }

    event.preventDefault();
    this.renderDrag(deltaX);
  }

  private resolvePendingGesture(deltaX: number, deltaY: number): void {
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    if (absoluteY > absoluteX && absoluteY > this.activationDistancePx) {
      this.gestureState = "rejected";
      return;
    }
    if (absoluteX < this.activationDistancePx) {
      return;
    }
    if (absoluteX <= absoluteY * this.horizontalLockRatio) {
      return;
    }

    this.gestureState = "dragging";
    this.swipeNavigationDragStarted.emit();
    this.renderer.setStyle(this.elementRef.nativeElement, "transition", "none");
  }

  private renderDrag(deltaX: number): void {
    const direction = this.getDirection(deltaX);
    const canSwipe = this.canSwipe(direction);
    const translatedX = canSwipe ? deltaX : deltaX * this.blockedDragResistance;
    this.renderer.setStyle(
      this.elementRef.nativeElement,
      "transform",
      `translateX(${translatedX}px)`
    );
  }

  private onTouchEnd(): void {
    if (this.gestureState !== "dragging") {
      this.resetGesture();
      return;
    }

    const deltaX = this.currentDeltaX;
    const direction = this.getDirection(deltaX);
    const shouldComplete =
      Math.abs(deltaX) >= this.completeDistancePx && this.canSwipe(direction);
    this.resetGesture();
    if (shouldComplete) {
      void this.completeGesture(direction);
      return;
    }
    this.cancelGesture();
  }

  private async completeGesture(
    direction: WorkModalNavigationDirection
  ): Promise<void> {
    this.preparing = true;
    this.centerWhilePreparing();
    const prepared = this.prepareSwipeNavigation
      ? await this.prepareSwipeNavigation(direction).catch(() => false)
      : true;
    if (this.destroyed) {
      return;
    }

    this.preparing = false;
    if (!prepared) {
      this.cancelGesture();
      return;
    }

    // The host keeps its current position: the navigation wrapper takes over from there
    // with the glued two-layer transition (no exit animation on this side).
    this.swipeNavigation.emit(direction);
  }

  private centerWhilePreparing(): void {
    const host = this.elementRef.nativeElement;
    this.renderer.setStyle(
      host,
      "transition",
      `transform ${this.cancelAnimationMs}ms ease-out`
    );
    this.renderer.setStyle(host, "transform", "translateX(0)");
  }

  private cancelGesture(): void {
    const host = this.elementRef.nativeElement;
    this.renderer.setStyle(
      host,
      "transition",
      `transform ${this.cancelAnimationMs}ms ease-out`
    );
    this.renderer.setStyle(host, "transform", "translateX(0)");
    this.cancelTimeout = setTimeout(() => {
      this.cancelTimeout = undefined;
      this.renderer.removeStyle(host, "transition");
      this.renderer.removeStyle(host, "transform");
      this.swipeNavigationDragSettled.emit();
    }, this.cancelAnimationMs);
  }

  private clearCancelAnimation(): void {
    if (this.cancelTimeout === undefined) {
      return;
    }
    clearTimeout(this.cancelTimeout);
    this.cancelTimeout = undefined;
    this.renderer.removeStyle(this.elementRef.nativeElement, "transition");
    this.renderer.removeStyle(this.elementRef.nativeElement, "transform");
  }

  private getDirection(deltaX: number): WorkModalNavigationDirection {
    return deltaX > 0 ? "previous" : "next";
  }

  private canSwipe(direction: WorkModalNavigationDirection): boolean {
    return direction === "previous" ? this.canSwipePrevious : this.canSwipeNext;
  }

  private resetGesture(): void {
    this.startX = 0;
    this.startY = 0;
    this.currentDeltaX = 0;
    this.gestureState = "idle";
  }
}
